import React, { memo, useCallback, useMemo } from 'react';
import { Typography, Button } from 'antd';
import { formatCurrency } from 'common/helpers/format.helper';
import plusIcon from 'app/assets/images/plus.svg';
import { IMenuItemWithCategoryNameAndType } from 'entities/Menu/Menu.models';
import { MenuItemImage } from 'entities/Menu/components/MenuItemImage';

interface IComponentProps {
  item: IMenuItemWithCategoryNameAndType;
  isDefaultMenu: boolean;
  onClick(item: IMenuItemWithCategoryNameAndType): void;
  isRowView?: boolean;
}

export const MenuItemCard = memo(function MenuItemCard({ item, isDefaultMenu, onClick, isRowView }: IComponentProps) {
  const { name, isEnable, price, image, description } = item;

  const isTemporarilyNotAvailable = !isDefaultMenu && !isEnable;
  const isClickable = !isTemporarilyNotAvailable;
  const showPrice = !isDefaultMenu && isEnable;

  const formattedPrice = useMemo(() => (showPrice ? formatCurrency(price) : ''), [showPrice, price]);

  const handleCardClick = useCallback(() => {
    if (!isClickable) {
      return;
    }

    onClick(item);
  }, [isClickable, onClick, item]);

  const handleButtonClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (!isClickable) {
        return;
      }

      onClick(item);
    },
    [isClickable, onClick, item],
  );

  const cardClassName = useMemo(() => {
    const base = `menu-card menu-card_height_fixed ${isClickable ? 'cursor-pointer' : ''}`;
    return isRowView ? `${base} menu-card_row` : `${base} d-flex d-flex_column d-flex_justify-between`;
  }, [isClickable, isRowView]);

  if (isRowView) {
    return (
      <div className={cardClassName} onClick={handleCardClick} role="button" tabIndex={0}>
        <div className="menu-card__row">
          <div className="menu-card__row-left">
            {image ? (
              <div className="menu-card__row-image">
                <MenuItemImage src={image} className="menu-card__image" alt={name || 'menu item'} />
              </div>
            ) : (
              <div className="menu-card__row-image menu-card__row-image_placeholder" />
            )}

            <div className="menu-card__row-title">
              {name ? <Typography.Paragraph className="menu-card__header mb-0">{name}</Typography.Paragraph> : null}

              {isTemporarilyNotAvailable ? <div className="menu-card__row-unavailable fs">Temporarily unavailable</div> : null}
            </div>
          </div>

          <div className="menu-card__row-right">
            {showPrice ? <div className="menu-card__row-price">{formattedPrice}</div> : null}

            {isClickable ? (
              <Button className="btn-opacity btn-opacity_gray menu-card__row-btn" onClick={handleButtonClick}>
                <img src={plusIcon} alt="open menu" className="height-full" />
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cardClassName} onClick={handleCardClick} role="button" tabIndex={0}>
      <div className="menu-card__content">
        <div>
          {name ? (
            <Typography.Paragraph ellipsis={{ rows: 3 }} className="menu-card__header mb-0">
              {name}
            </Typography.Paragraph>
          ) : null}

          {!isRowView && description ? (
            <Typography.Paragraph ellipsis={{ rows: 3 }} className="menu-card__description mb-0">
              {description}
            </Typography.Paragraph>
          ) : null}
        </div>

        {image ? (
          <div className="menu-card__image-wrapper">
            <MenuItemImage src={image} className="menu-card__image" alt={name || 'menu item'} />
          </div>
        ) : null}
      </div>

      <div className="menu-card__footer">
        {isClickable ? (
          <Button className="btn-opacity btn-opacity_gray" onClick={handleButtonClick}>
            <img src={plusIcon} alt="open menu" className="height-full" />
          </Button>
        ) : null}

        {showPrice ? <div className="mt-2 ml-5 font-weight-bold fs">{formattedPrice}</div> : null}
      </div>

      {isTemporarilyNotAvailable ? <div className="menu-card__footer fs">Temporarily unavailable</div> : null}
    </div>
  );
});
